import * as React from 'react';
import {
  Button,
  Card,
  Collapsible,
  IconButton,
  Stack,
  Text,
  Tooltip,
} from '@fragments-sdk/ui';
import { Check, Copy } from '@phosphor-icons/react';
import { useCopyAction } from './mcp-developer-setup-helpers';
import s from './mcp-developer-setup.module.scss';

/** A titled panel — the page is a vertical stack of these. */
export function SectionCard({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card variant="panel" padding="none" as="section">
      <Card.Header divided className={s.sectionHeader}>
        <Stack
          direction={{ base: 'column', md: 'row' }}
          gap="md"
          align="stretch"
          justify="between"
        >
          <Stack gap="xs">
            <Text as="h2" size="sm" weight="semibold">
              {title}
            </Text>
            {description ? (
              <Text as="p" size="xs" color="tertiary" className={s.sectionDesc}>
                {description}
              </Text>
            ) : null}
          </Stack>
          {actions ? (
            <Stack direction="row" gap="sm" align="center" wrap>
              {actions}
            </Stack>
          ) : null}
        </Stack>
      </Card.Header>
      <Card.Body className={s.sectionBody}>{children}</Card.Body>
    </Card>
  );
}

/** Copy-to-clipboard button with a transient "Copied" confirmation. */
export function CopyButton({
  getValue,
  label,
  variant = 'secondary',
  fullWidth,
  disabled,
}: {
  getValue: () => string;
  label: string;
  variant?: 'primary' | 'secondary';
  fullWidth?: boolean;
  disabled?: boolean;
}) {
  const { copied, copy } = useCopyAction();
  return (
    <Button
      variant={variant}
      size="sm"
      fullWidth={fullWidth}
      disabled={disabled}
      onClick={() => copy(getValue())}
    >
      {copied ? <Check size={14} weight="bold" /> : <Copy size={14} />}
      {copied ? 'Copied' : label}
    </Button>
  );
}

/** A labelled, monospace value with an inline copy control. */
export function CopyField({
  label,
  value,
  helper,
  copyable = true,
}: {
  label: string;
  value: string;
  helper?: string;
  copyable?: boolean;
}) {
  const { copied, copy } = useCopyAction();
  return (
    <div className={s.copyField}>
      <Text
        as="span"
        size="xs"
        weight="semibold"
        color="tertiary"
        className={s.copyFieldLabel}
      >
        {label}
      </Text>
      <Stack direction="row" gap="sm" align="center" justify="between">
        <Text
          as="code"
          size="xs"
          font="mono"
          color="primary"
          className={s.copyFieldValue}
        >
          {value}
        </Text>
        {copyable ? (
          <Tooltip content={copied ? 'Copied' : `Copy ${label.toLowerCase()}`}>
            <IconButton
              variant="ghost"
              size="sm"
              aria-label={`Copy ${label.toLowerCase()}`}
              onClick={() => copy(value)}
            >
              {copied ? <Check size={13} weight="bold" /> : <Copy size={13} />}
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
      {helper ? (
        <Text as="p" size="xs" color="tertiary" className={s.copyFieldHelper}>
          {helper}
        </Text>
      ) : null}
    </div>
  );
}

/** One way to connect an editor: a logo, a blurb, and a single action. */
export function ClientCard({
  icon,
  title,
  description,
  command,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  command?: string;
  children: React.ReactNode;
}) {
  return (
    <Card variant="outlined" padding="md" as="article" className={s.clientCard}>
      <Stack gap="sm" className={s.clientCardStack}>
        <Stack direction="row" gap="sm" align="center">
          <Text as="span" className={s.clientIcon} aria-hidden>
            {icon}
          </Text>
          <Text as="h3" size="sm" weight="semibold">
            {title}
          </Text>
        </Stack>
        <Text as="p" size="xs" color="secondary" className={s.clientDesc}>
          {description}
        </Text>
        {command ? (
          <Text
            as="code"
            size="xs"
            font="mono"
            color="primary"
            className={s.clientCommand}
          >
            {command}
          </Text>
        ) : null}
        <Stack className={s.clientAction}>{children}</Stack>
      </Stack>
    </Card>
  );
}

/** Fold-away reference for the OAuth discovery metadata most clients auto-resolve. */
export function DiscoveryDisclosure({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Collapsible className={s.discovery}>
      <Collapsible.Trigger className={s.discoveryTrigger}>
        OAuth discovery details
      </Collapsible.Trigger>
      <Collapsible.Content>
        <Stack gap="sm" className={s.discoveryContent}>
          {children}
        </Stack>
      </Collapsible.Content>
    </Collapsible>
  );
}
